import React, { useState, useRef, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileSizeLimit } from '@/hooks/useFileSizeLimit';
import { checkFile } from '../utils/fileGuards';

interface DragDropFileUploadProps {
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  accept?: string;
  multiple?: boolean;
  selectedFiles: File[];
  onRemoveFile?: (index: number) => void;
  onClearAll?: () => void;
  inputId?: string;
  dropLabelKeys?: { drop: string; dropOr: string; namespace: string };
  showDropZone?: boolean;
  disabled?: boolean;
  hideSelectedFilesHeader?: boolean;
}

const DragDropFileUpload: React.FC<DragDropFileUploadProps> = ({
  onFileChange,
  fileInputRef,
  accept = ".csv,.txt",
  multiple = true,
  selectedFiles,
  onRemoveFile,
  onClearAll,
  inputId = 'file-upload',
  dropLabelKeys,
  showDropZone = true,
  disabled = false,
  hideSelectedFilesHeader = false,
}) => {
  const { t } = useTranslation('dataUpload');
  const { t: tDrop } = useTranslation(dropLabelKeys?.namespace ?? 'dataUpload');
  const { maxFileSizeBytes, formattedMaxSize } = useFileSizeLimit();
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragOut = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const allowed = accept.split(',').map((s) => s.trim());

      // Validate every dropped file before dispatching
      for (const file of files) {
        const check = checkFile(file, allowed, maxFileSizeBytes);
        if (check.kind === 'bad_extension') {
          alert(
            t('errorMessages.fileExtensionNotAllowed', {
              name: file.name,
              extension: check.extension,
              allowed: allowed.join(', '),
            })
          );
          return;
        }
        if (check.kind === 'too_large') {
          alert(
            t('errorMessages.fileSizeExceeded', {
              name: file.name,
              size: (check.sizeBytes / (1024 * 1024)).toFixed(2),
              maxSize: formattedMaxSize,
            })
          );
          return;
        }
        if (check.kind === 'empty') {
          alert(t('errorMessages.fileEmpty', { name: file.name }));
          return;
        }
      }

      // All valid — dispatch the synthetic change event
      const dataTransfer = new DataTransfer();
      files.forEach((file) => dataTransfer.items.add(file));

      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        const event = new Event('change', { bubbles: true });
        fileInputRef.current.dispatchEvent(event);
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRemoveFile = (index: number) => {
    if (onRemoveFile) {
      onRemoveFile(index);
    }
  };

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    }
  };

  return (
    <div className="w-full">
      {showDropZone && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-4 sm:p-6 md:p-8 text-center transition-all duration-200
            ${isDragging
              ? 'border-primary bg-accent scale-[1.02] sm:scale-105'
              : 'border-border hover:border-border hover:bg-muted'
            }
            ${disabled ? 'pointer-events-none opacity-50' : ''}
          `}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          aria-disabled={disabled}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={onFileChange}
            accept={accept}
            multiple={multiple}
            className="hidden"
            id={inputId}
            disabled={disabled}
          />

          <div className="flex flex-col items-center space-y-2 sm:space-y-4">
            {/* Upload Icon */}
            <svg
              className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>

            <div className="space-y-1.5 sm:space-y-2">
              <p className="text-sm sm:text-base md:text-lg font-medium text-foreground">
                {isDragging
                  ? tDrop(dropLabelKeys?.drop ?? 'upload.dropFiles')
                  : tDrop(dropLabelKeys?.dropOr ?? 'upload.dropFilesOr')}
              </p>
              <label
                htmlFor={inputId}
                className="cursor-pointer inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
              >
                {t(multiple ? 'upload.selectFiles' : 'upload.selectFile')}
              </label>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {accept.trim() === '.csv'
                  ? t('upload.acceptedFormatsCsvOnly', { maxSize: formattedMaxSize })
                  : t('upload.acceptedFormats', { maxSize: formattedMaxSize })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* File List */}
      {!hideSelectedFilesHeader && selectedFiles.length > 0 && (
        <div className="mt-3 sm:mt-4 space-y-2">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-xs sm:text-sm font-medium text-foreground">
              {t(multiple ? 'upload.selectedFiles' : 'upload.selectedFile')} ({selectedFiles.length})
            </h3>
            {onClearAll && (
              <button
                onClick={handleClearAll}
                className="text-xs sm:text-sm text-red-600 hover:text-red-800 hover:underline"
                type="button"
              >
                {t('selectedFiles.clearAll', { ns: 'dataAdjustments' })}
              </button>
            )}
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 sm:p-3 bg-muted rounded-lg border border-border"
              >
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <svg
                    className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-foreground truncate">{file.name}</p>
                    <p className="text-[10px] sm:text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="text-red-500 hover:text-red-700 transition-colors ml-2 flex-shrink-0"
                  aria-label={t('upload.removeFile')}
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <div className="text-[10px] sm:text-sm text-muted-foreground mt-2">
            {t('upload.totalSize')}: {formatFileSize(selectedFiles.reduce((acc, file) => acc + file.size, 0))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DragDropFileUpload;